import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { handleApiError } from "../utils/serviceUtils/errorHandler";

// ApiService class to abstract HTTP operations using Axios
class ApiService {
  private axiosInstance: AxiosInstance; // Axios instance for making HTTP requests
  private baseURL: string = process.env.REACT_APP_API_URL || "";

  // Constructor to initialize the Axios instance with default configurations
  constructor(baseURL?: string, timeout?: number, baseToken?: string) {
    this.baseURL = baseURL || process.env.REACT_APP_API_URL || "";
    this.axiosInstance = axios.create({
      baseURL: this.baseURL, // Base URL for all API requests
      timeout: timeout || 100000, // Default timeout
      headers: {
        Accept: "application/json",
      },
    });

    // Axios request interceptor for dynamic token management
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        config.headers.Authorization = `Bearer ${
          baseToken ? baseToken : token
        }`;
        return config;
      },
      (error) => Promise.reject(error), // Handle request errors
    );

    // Axios response interceptor for centralized error handling
    this.axiosInstance.interceptors.response.use(
      (response) => response, // Return successful responses as-is
      (error) => handleApiError(error), // Delegate error handling
    );
  }

  /**
   * Private method to handle HTTP requests.
   * This method is reused by public methods like `get`, `post`, `put`, and `delete`.
   * @param method - HTTP method (GET, POST, PUT, DELETE)
   * @param url - API endpoint
   * @param data - Request payload (optional)
   * @param config - Axios request configuration (optional)
   * @returns A Promise that resolves with the response data
   */
  private async request<T>(
    method: "get" | "post" | "put" | "delete" | "patch",
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    try {
      const fullURL = `${this.baseURL}${url}`;
      const response: AxiosResponse<T> = await this.axiosInstance.request({
        method, // HTTP method
        url: fullURL, // API endpoint
        data, // Request payload (optional)
        ...config, // Additional request configuration
      });
      return response.data; // Return only the response data
    } catch (error) {
      throw error; // Rethrow the error for external handling
    }
  }

  /**
   * Public method to perform a GET request.
   * @param url - API endpoint
   * @param config - Axios request configuration (optional)
   * @returns A Promise that resolves with the response data
   */
  public async get<T>(url?: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>("get", url || "", undefined, config);
  }

  /**
   * Public method to perform a POST request.
   * @param url - API endpoint
   * @param data - Request payload
   * @param config - Axios request configuration (optional)
   * @returns A Promise that resolves with the response data
   */
  public async post<T>(
    url?: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>("post", url || "", data, config);
  }

  /**
   * Public method to perform a PUT request.
   * @param url - API endpoint
   * @param data - Request payload
   * @param config - Axios request configuration (optional)
   * @returns A Promise that resolves with the response data
   */
  public async put<T>(
    url: string,
    data: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>("put", url, data, config);
  }

  /**
   * Public method to perform a DELETE request.
   * @param url - API endpoint
   * @param config - Axios request configuration (optional)
   * @returns A Promise that resolves with the response data
   */
  public async delete<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>("delete", url, data, config);
  }

  /**
   * Public method to perform a PATCH request.
   * @param url - API endpoint
   * @param data - Request payload
   * @param config - Axios request configuration (optional)
   * @returns A Promise that resolves with the response data
   */
  public async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>("patch", url, data, config);
  }
}

export default ApiService;
